import React from 'react'
import { ChevronDown } from 'lucide-react'

export interface PhoneSheetProps {
  /** Optional close handler — when provided, a chevron-down close button renders in the header. */
  onClose?: () => void
  /** Title shown left-aligned in the header (e.g. "Phone"). */
  title?: string
  /** Renders below the title row — typically the segmented tab control. */
  header?: React.ReactNode
  /** Body content. Scrolls if it exceeds available height. */
  children: React.ReactNode
  /** Optional sticky footer (above safe-area-bottom). */
  footer?: React.ReactNode
  /** Visual variant. 'sheet' = mobile full-screen, 'panel' = desktop popover-sized card. */
  variant?: 'sheet' | 'panel'
  /** Shell test id. */
  'data-testid'?: string
}

/**
 * Phase 2D — uniform sheet shell for ALL Phone sub-surfaces.
 *
 * Every Phone sub-tab (Dialer, Contacts, Recents, the active-call view,
 * the post-call card on mobile) renders inside this shell so they share
 * the same background opacity, border treatment, header pattern, and
 * footer/CTA placement. No more one-off transparencies or one-off
 * heights between tabs.
 *
 * - `variant="sheet"` → mobile full-screen, fills 100dvh, safe-area aware,
 *    solid surface (--midnight-surface).
 * - `variant="panel"` → desktop popover card, solid surface, fixed width.
 *
 * Notes:
 * - The body region is the only scroller; header + footer stay fixed.
 * - Inner uniform min-height for tab content is enforced by callers
 *   (they wrap with min-height inline) so the same shell can host
 *   short content (DialPad) and long content (CallHistory) at the
 *   same visual height.
 */
export function PhoneSheet({
  onClose,
  title,
  header,
  children,
  footer,
  variant = 'sheet',
  ...rest
}: PhoneSheetProps): React.ReactElement {
  const isSheet = variant === 'sheet'

  const containerClass = isSheet ? 'ds-phone-sheet' : 'ds-tile-solid'
  const containerStyle: React.CSSProperties = isSheet
    ? {} // ds-phone-sheet handles position + safe-area + animation
    : {
        display: 'flex',
        flexDirection: 'column',
        // Desktop panel — match desktop-FAB widget width
        width: '100%',
      }

  return (
    <div
      className={containerClass}
      style={containerStyle}
      data-testid={rest['data-testid']}
      role={isSheet ? 'dialog' : undefined}
      aria-modal={isSheet ? true : undefined}
      aria-label={isSheet && title ? title : undefined}
    >
      {/* Header — solid surface, hairline divider */}
      {(title || onClose || header) && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            borderBottom: '0.5px solid var(--midnight-border)',
            // Phone is comms: a soft magenta-to-violet wash behind the title.
            background:
              'radial-gradient(90% 160% at 0% 0%, rgb(var(--rose-rgb) / 0.16), transparent 60%), radial-gradient(70% 140% at 100% 0%, rgb(var(--lavender-rgb) / 0.14), transparent 65%), var(--midnight-surface)',
            flexShrink: 0,
          }}
        >
          {(title || onClose) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                minHeight: 48,
              }}
            >
              <span
                style={{
                  // iOS title: the word, not a shouted eyebrow.
                  fontSize: 20,
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  color: 'rgb(var(--fg-rgb))',
                }}
              >
                {title}
              </span>
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close phone"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 'var(--r-pill)',
                    border: '0.5px solid var(--midnight-border)',
                    background: 'var(--midnight-surface-2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'var(--midnight-text2)',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <ChevronDown style={{ width: 16, height: 16 }} />
                </button>
              )}
            </div>
          )}
          {header && (
            <div style={{ padding: '0 12px 10px' }}>
              {header}
            </div>
          )}
        </div>
      )}

      {/* Body — single scroller */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          background: 'var(--midnight-surface)',
        }}
      >
        {children}
      </div>

      {/* Footer — sticky above safe-area, solid surface */}
      {footer && (
        <div
          style={{
            flexShrink: 0,
            borderTop: '0.5px solid var(--midnight-border)',
            background: 'var(--midnight-surface)',
            padding: '12px 16px',
          }}
        >
          {footer}
        </div>
      )}
    </div>
  )
}
